<template>
    <div class="w-full">
        <div class="block w-full">
            <Tabs v-model="platform" :options="PLATFORMS" class="mb-4" secondary />
        </div>

        <div v-if="platform === 'openai'">
            <CopyCode :code="openai_code" label="OpenAI" class="mb-4" page="MCP OpenAI" />
            <ExternalLink v-if="include_links" href="https://platform.openai.com/docs/guides/mcp"
                title="OpenAI MCP Documentation" />
        </div>

        <div v-if="platform === 'anthropic'">
            <CopyCode :code="anthropic_code" label="Anthropic" class="mb-4" page="MCP Anthropic" />
            <ExternalLink v-if="include_links" href="https://docs.anthropic.com/en/docs/build-with-claude/mcp"
                title="Anthropic MCP Documentation" />
        </div>

        <div v-if="platform === 'gemini'">
            <CopyCode :code="gemini_code" label="Google Gemini" class="mb-4" page="MCP Gemini" />
            <ExternalLink v-if="include_links" href="https://ai.google.dev/gemini-api/docs/function-calling"
                title="Gemini Function Calling Documentation" />
        </div>

        <div v-if="platform === 'cohere'">
            <CopyCode :code="cohere_code" label="Cohere" class="mb-4" page="MCP Cohere" />
            <ExternalLink v-if="include_links" href="https://docs.cohere.com/docs/tool-use"
                title="Cohere Tool Use Documentation" />
        </div>

        <div v-if="platform === 'grok'">
            <CopyCode :code="grok_code" label="Grok & Groq" class="mb-4" page="MCP Grok" />
            <div v-if="include_links" class="flex gap-6">
                <ExternalLink href="https://docs.x.ai/docs" title="Grok Documentation" />
                <ExternalLink href="https://console.groq.com/docs/tool-use" title="Groq Documentation" />
            </div>
        </div>

        <div v-if="platform === 'claude-online'">
            <CopyCode :code="claude_online_code" label="Claude.ai" class="mb-4" page="MCP Claude Online" />
            <div class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Go to <strong>claude.ai</strong> → <strong>Settings</strong> → <strong>Integrations</strong> → Click
                <strong>"Add custom integration"</strong>
            </div>
            <ExternalLink v-if="include_links" href="https://claude.ai" title="Claude.ai" />
        </div>

        <div v-if="platform === 'claude-desktop'">
            <CopyCode :code="claude_desktop_code" label="Claude Desktop" class="mb-4" page="MCP Claude Desktop" />
            <div class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Edit the <strong>claude_desktop_config.json</strong> file with the configuration above.
            </div>
            <ExternalLink v-if="include_links" href="https://claude.ai/download" title="Download Claude Desktop" />
        </div>

        <div v-if="platform === 'cursor'">
            <CopyCode :code="cursor_code" label="Cursor" class="mb-4" page="MCP Cursor" />
            <div v-if="include_links" class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Navigate to <strong>Cursor</strong> → <strong>Settings</strong> → <strong>Cursor Settings</strong> →
                <strong>MCP</strong> and edit the MCP configuration.
            </div>
            <ExternalLink v-if="include_links" href="https://cursor.sh" title="Cursor IDE" />
        </div>
    </div>
</template>

<script lang="ts">
const PLATFORMS = [
    'openai',
    'anthropic',
    'gemini',
    'cohere',
    'grok',
    // 'claude-online',
    // 'claude-desktop',
    // 'cursor',
];

export default {
    props: {
        token: String,
        connection: String,
        include_links: Boolean,
    },
    setup() {
        return {
            PLATFORMS,
        };
    },
    data() {
        return {
            platform: 'openai',
            openai_code: '',
            anthropic_code: '',
            gemini_code: '',
            cohere_code: '',
            grok_code: '',
            claude_online_code: '',
            claude_desktop_code: '',
            cursor_code: '',
        };
    },
    watch: {
        token() {
            this.updateCode();
        },
        connection() {
            this.updateCode();
        },
    },
    methods: {
        getUrl() {
            const connection = this.connection || 'YOUR_CONNECTION_ID';
            return `https://mcp-api.unified.to/mcp?connection=${connection}`;
        },
        getToolsUrl(type: string) {
            return `https://mcp-api.unified.to/tools?type=${type}&connection=${this.connection || 'YOUR_CONNECTION_ID'}`;
        },
        updateCode() {
            // The workspace API key goes in the Authorization header, not the URL.
            const url = this.getUrl();
            const token = this.token || 'YOUR_TOKEN';

            // OpenAI - Streamable HTTP
            this.openai_code = `resp = client.responses.create(
    model="gpt-4.1",
    tools=[{
        "type": "mcp",
        "server_label": "unifiedMCP",
        "server_url": "${url}",
        "headers": {"Authorization": "Bearer ${token}"},
        "require_approval": "never",
        "allowed_tools": [],
    }],
    input="list the candidates and then analyse the resumes from their applications",
)`;

            // Anthropic - MCP connector (sends authorization_token as a Bearer header)
            this.anthropic_code = `const completion = await anthropic.beta.messages.create({
    model: 'claude-opus-5-5',
    max_tokens: 1024,
    messages: [
        {
            role: 'user',
            content: 'list the candidates and analyse their resumes',
        },
    ],
    mcp_servers: [
        {
            type: 'url',
            url: "${url}",
            name: 'unifiedMCP',
            authorization_token: "${token}",
        },
    ],
    tools: [{ type: 'mcp_toolset', mcp_server_name: 'unifiedMCP' }],
    betas: ['mcp-client-2025-11-20'],
});`;

            // Gemini
            this.gemini_code = `// First, get tools from Unified API
const toolsResponse = await fetch('${this.getToolsUrl('gemini')}', {
    headers: { Authorization: 'Bearer ${token}' },
});
const tools = await toolsResponse.json();

// Then use with Gemini
const completion = await gemini.models.generateContent({
    model: 'gemini-pro',
    contents: 'list the candidates and analyse their resumes',
    config: {
        tools: tools
    },
});`;

            // Cohere
            this.cohere_code = `// First, get tools from Unified API
const toolsResponse = await fetch('${this.getToolsUrl('cohere')}', {
    headers: { Authorization: 'Bearer ${token}' },
});
const tools = await toolsResponse.json();

// Then use with Cohere
const response = co.chat({
    model: "command-a-03-2025",
    messages: messages,
    tools: tools
});`;

            // Grok & Groq
            this.grok_code = `// First, get tools from Unified API
const toolsResponse = await fetch('${this.getToolsUrl('grok')}', {
    headers: { Authorization: 'Bearer ${token}' },
});
const tools = await toolsResponse.json();

const response = client.chat.completions.create({
    model: 'grok-beta',
    messages: messages,
    tools: tools,
    tool_choice: 'auto',
});`;

            // Claude.ai online: custom connectors take only a URL, so the token stays in it here
            this.claude_online_code = `${url}&token=${token}`;

            // Claude Desktop
            this.claude_desktop_code = `{
    "mcpServers": {
        "unified-mcp": {
            "command": "npx",
            "args": [
                "-y",
                "mcp-remote",
                "${url}",
                "--header",
                "Authorization:\${AUTH_HEADER}"
            ],
            "env": { "AUTH_HEADER": "Bearer ${token}" }
        }
    }
}`;

            // Cursor
            this.cursor_code = `{
    "mcpServers": {
        "unified-mcp": {
            "url": "${url}",
            "headers": { "Authorization": "Bearer ${token}" }
        }
    }
}`;
        },
    },
    mounted() {
        this.updateCode();
    },
};
</script>